import { Injectable, signal } from '@angular/core';

export interface EventDraftContext {
    eventId: string;
    registrationId: string;
    slotId?: number;
    slotLabel: string;
    pointsLimit?: number;
    orderDiceLimit?: number;
    riftDiceLimit?: number;
}

/**
 * Carries "create a new force for this event slot" from the event detail page's Create List
 * button into whichever page actually finalises force creation, without threading it through
 * router query params (this app's force-creation flow spans several route segments before a
 * force actually exists to attach anything to). Read once by the creation flow and cleared
 * immediately after — a stale value here would otherwise silently tag the NEXT unrelated force
 * a user creates with the wrong event.
 */
@Injectable({ providedIn: 'root' })
export class ForceCreationContextService {
    private context = signal<EventDraftContext | null>(null);

    set(context: EventDraftContext) {
        this.context.set(context);
    }

    /** Reads and clears in one call — every consumer needs the value exactly once. */
    consume(): EventDraftContext | null {
        const value = this.context();
        this.context.set(null);
        return value;
    }

    /**
     * Non-destructive read, for UI that needs to reflect a pending context before the creation
     * flow actually consumes it — e.g. pre-filling and locking the limit inputs in the
     * create-force dialog so the user sees the event's limits before the force even exists.
     */
    peek(): EventDraftContext | null {
        return this.context();
    }
}
